import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';
import { KvkkNoticeModal, TermsModal, PrivacyModal } from './LegalModals';
import BrandLogo from './BrandLogo';
import { TwoFactorCodeForm, TwoFactorSetup, SliderPuzzle, PUZZLE_REQUIRED_CODE } from '../modules/security';
import { apiJson, QrImage, twoFactorStrings, TFA_BUTTONS } from './twoFactorShared';
import { SESSION_MARKER } from '../utils/apiFetch';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.32-8.16 2.32-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const PUZZLE_STRINGS_EN = {
  title: 'Security check',
  instruction: 'Slide the piece into the gap to continue.',
  refresh: 'New puzzle',
  verifying: 'Checking…',
  success: 'Verified',
  failed: "The piece didn't fit, try again.",
  loadFailed: 'Could not load the check.',
  sliderLabel: 'Slide the puzzle piece',
  cancel: 'Cancel',
};

export default function Login({ onLogin }) {
  const { t, lang } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab]   = useState('login');

  // Giriş state
  const [loginId,   setLoginId]     = useState('');
  const [password,  setPassword]    = useState('');
  const [showPass,  setShowPass]    = useState(false);

  // Kayıt state
  const [regName,   setRegName]     = useState('');
  const [regEmail,  setRegEmail]    = useState('');
  const [regPass,   setRegPass]     = useState('');
  const [regShowP,  setRegShowP]    = useState(false);
  const [consent1,  setConsent1]    = useState(false);
  const [consent2,  setConsent2]    = useState(false);
  const [conEmail,  setConEmail]    = useState(false);
  const [conSms,    setConSms]      = useState(false);
  const [agreeKvkk,  setAgreeKvkk]  = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showKvkk,    setShowKvkk]    = useState(false);
  const [showTerms,   setShowTerms]   = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [loading,   setLoading]     = useState(false);
  const [error,     setError]       = useState('');
  const [success,   setSuccess]     = useState('');

  // İki adımlı doğrulama (madde 12): şifre doğruysa sunucu oturum yerine kod adımı
  // { kind: 'verify', challengeToken, method, phoneHint } ya da zorunlu kurulum
  // { kind: 'setup', challengeToken, smsAvailable } döndürebilir.
  const [tfa, setTfa] = useState(null);
  // Giriş yapbozu (şüpheli durumda — backend 428 PUZZLE_REQUIRED): çözülünce giriş geçiş belgesiyle tekrarlanır.
  const [puzzle, setPuzzle] = useState(false);
  const enrolledLogin = useRef(null);

  // Oturum backend'in koyduğu HttpOnly çerezde (madde 17); yanıtta token yok.
  const finishLogin = (data) => {
    localStorage.setItem('ts_role',  data.role);
    localStorage.setItem('ts_type',  data.user_type || 'system');
    localStorage.setItem('ts_sub',   data.subscription || 'active');
    localStorage.setItem('ts_name',  data.name || '');
    onLogin(SESSION_MARKER, data.role, data.user_type || 'system', data.subscription || 'active', data.name || '');
  };

  const verifyCode = async (code) => {
    setError(''); setLoading(true);
    try { finishLogin(await apiJson('POST', '/api/auth/2fa/verify', { challengeToken: tfa.challengeToken, code })); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const resendCode = async () => {
    setError('');
    try { const d = await apiJson('POST', '/api/auth/2fa/resend', { challengeToken: tfa.challengeToken }); setSuccess(d.message); }
    catch (err) { setError(err.message); }
  };

  const cancelTfa = () => { setTfa(null); setError(''); setSuccess(''); setPassword(''); };

  // Google OAuth başarısız olduğunda App.js, buraya state.authError ile yönlendirir
  // (başarı durumunda token'lı URL App.js seviyesinde işlenip doğrudan /panel'e gidilir).
  useEffect(() => {
    if (location.state?.authError) setError(t(location.state.authError === 'blocked' ? 'login.accountBlocked' : 'login.googleFailed'));
  }, []); // eslint-disable-line

  const handleLogin = (e) => { e.preventDefault(); submitLogin(); };

  const submitLogin = async (puzzleToken) => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res  = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: loginId, email: loginId, password, ...(puzzleToken ? { puzzleToken } : {}) }),
      });
      const data = await res.json();
      if (res.status === 428 && data.code === PUZZLE_REQUIRED_CODE) { setPuzzle(true); return; }
      setPuzzle(false);
      if (!res.ok) { setError(data.error || t('login.loginFailed')); return; }
      if (data.twoFactorRequired) { setTfa({ kind: 'verify', challengeToken: data.challengeToken, method: data.method, phoneHint: data.phoneHint }); return; }
      if (data.twoFactorSetupRequired) { setTfa({ kind: 'setup', challengeToken: data.challengeToken, smsAvailable: !!data.smsAvailable }); return; }
      finishLogin(data);
    } catch { setError(t('login.serverUnreachable')); }
    finally  { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res  = await fetch('/api/auth/register', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: regName, email: regEmail, password: regPass, legal_accepted: agreeKvkk && agreeTerms }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || t('login.registerFailed')); return; }
      localStorage.setItem('ts_role',  data.role);
      localStorage.setItem('ts_type',  data.user_type || 'user');
      localStorage.setItem('ts_sub',   data.subscription || 'inactive');
      localStorage.setItem('ts_name',  data.name || '');
      onLogin(SESSION_MARKER, data.role, data.user_type || 'user', data.subscription || 'inactive', data.name || '');
    } catch { setError(t('login.serverUnreachable')); }
    finally  { setLoading(false); }
  };

  const handleGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* Logo */}
        <Link to="/">
          <BrandLogo className="auth-logo-wrap" imgClassName="auth-logo-img" fallbackClassName="auth-logo-fallback" />
        </Link>

        {/* Başlık */}
        <h1 className="auth-title">{t('login.greeting')}</h1>
        {!tfa && <>
        <p className="auth-subtitle">
          <span
            className={activeTab === 'login' ? 'auth-sub-link active' : 'auth-sub-link'}
            onClick={() => { setActiveTab('login'); setError(''); }}
          >{t('login.login')}</span>
          {' '}{t('login.or')}{' '}
          <span
            className={activeTab === 'register' ? 'auth-sub-link active' : 'auth-sub-link'}
            onClick={() => { setActiveTab('register'); setError(''); }}
          >{t('login.register')}</span>
        </p>

        {/* Tab Bar */}
        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => { setActiveTab('login'); setError(''); }}
          >{t('login.loginTab')}</button>
          <button
            className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => { setActiveTab('register'); setError(''); }}
          >{t('login.registerTab')}</button>
        </div>
        </>}

        {/* Hata / Başarı */}
        {error   && <div className="auth-alert auth-alert-error">{error}</div>}
        {success && <div className="auth-alert auth-alert-success">{success}</div>}

        {/* ── İKİ ADIMLI DOĞRULAMA (madde 12) ── */}
        {tfa?.kind === 'verify' && (
          <div className="auth-form auth-tfa">
            <TwoFactorCodeForm
              method={tfa.method}
              phoneHint={tfa.phoneHint}
              busy={loading}
              onSubmit={verifyCode}
              onResend={tfa.method === 'SMS' ? resendCode : undefined}
              onBack={cancelTfa}
              strings={twoFactorStrings(lang)}
              buttons={TFA_BUTTONS}
            />
          </div>
        )}
        {tfa?.kind === 'setup' && (
          <div className="auth-form auth-tfa">
            <p className="auth-pass-hint">{t('login.tfaSetupRequired')}</p>
            <TwoFactorSetup
              smsAvailable={tfa.smsAvailable}
              start={(method, phone) => apiJson('POST', '/api/auth/2fa/setup', { method, phone, challengeToken: tfa.challengeToken })}
              enable={async (code) => {
                const d = await apiJson('POST', '/api/auth/2fa/enable', { code, challengeToken: tfa.challengeToken });
                enrolledLogin.current = d;
                return d.backupCodes;
              }}
              onDone={() => enrolledLogin.current && finishLogin(enrolledLogin.current)}
              onError={(err) => setError(err?.message || String(err))}
              onCancel={cancelTfa}
              cancelLabel={t('login.tfaCancel')}
              renderQr={(url) => <QrImage text={url} />}
              renderPhoneInput={(value, onChange) => (
                <input className="auth-input" type="tel" placeholder="5XX XXX XX XX" value={value} onChange={(e) => onChange(e.target.value)} />
              )}
              strings={twoFactorStrings(lang)}
              buttons={TFA_BUTTONS}
            />
          </div>
        )}

        {/* ── GİRİŞ FORMU ── */}
        {!tfa && activeTab === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <input
              className="auth-input"
              type="text"
              placeholder={t('login.loginIdPlaceholder')}
              value={loginId}
              onChange={e => setLoginId(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <div className="auth-input-wrap">
              <input
                className="auth-input"
                type={showPass ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button type="button" className="auth-eye" onClick={() => setShowPass(p => !p)}>
                {showPass ? '👁' : '🙈'}
              </button>
            </div>
            <div className="auth-forgot-row">
              <span className="auth-forgot">{t('login.forgotPassword')}</span>
            </div>
            {puzzle ? (
              <SliderPuzzle
                load={() => apiJson('GET', '/api/auth/puzzle')}
                verify={(id, x, track) => apiJson('POST', '/api/auth/puzzle/verify', { id, x, track })}
                onSolved={(token) => submitLogin(token)}
                onCancel={() => setPuzzle(false)}
                errorMessage={(err) => err?.message}
                strings={lang === 'en' ? PUZZLE_STRINGS_EN : undefined}
              />
            ) : (
              <button type="submit" className="auth-btn-primary" disabled={loading}>
                {loading ? t('login.loggingIn') : t('login.loginButton')}
              </button>
            )}
          </form>
        )}

        {/* ── KAYIT FORMU ── */}
        {!tfa && activeTab === 'register' && (
          <form onSubmit={handleRegister} className="auth-form">
            <input
              className="auth-input"
              type="text"
              placeholder={t('login.fullNamePlaceholder')}
              value={regName}
              onChange={e => setRegName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
            />
            <input
              className="auth-input"
              type="email"
              placeholder={t('login.emailPlaceholder')}
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <div className="auth-input-wrap">
              <input
                className="auth-input"
                type={regShowP ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                value={regPass}
                onChange={e => setRegPass(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button type="button" className="auth-eye" onClick={() => setRegShowP(p => !p)}>
                {regShowP ? '👁' : '🙈'}
              </button>
            </div>
            <p className="auth-pass-hint">{t('login.passwordHint')}</p>

            {/* TrendSniper platform güvencesi — kullanıcının hiçbir zaman yatırım tavsiyesi almadığını,
                izni dışında hiçbir işlem/ücret yapılmadığını açıkça ve öne çıkan şekilde bildirir. */}
            <div className="auth-platform-notice">
              <div className="auth-platform-notice-title">{t('login.platformNoticeTitle')}</div>
              <ul className="auth-platform-notice-list">
                <li>{t('login.platformNoticeItem1')}</li>
                <li>{t('login.platformNoticeItem2')}</li>
                <li>{t('login.platformNoticeItem3')}</li>
                <li>{t('login.platformNoticeItem4')}</li>
              </ul>
            </div>

            {/* KVKK onay 1 */}
            <div className="auth-consent-box">
              <label className="auth-consent-row">
                <input type="checkbox" checked={consent1} onChange={e => setConsent1(e.target.checked)} />
                <span>{t('login.consent1')}</span>
              </label>
              <div className="auth-consent-sub">
                <label className="auth-consent-sub-item">
                  <input type="checkbox" checked={conEmail} onChange={e => setConEmail(e.target.checked)} /> {t('login.consentEmail')}
                </label>
                <label className="auth-consent-sub-item">
                  <input type="checkbox" checked={conSms} onChange={e => setConSms(e.target.checked)} /> {t('login.consentSms')}
                </label>
              </div>
            </div>

            {/* KVKK onay 2 */}
            <div className="auth-consent-box">
              <label className="auth-consent-row">
                <input type="checkbox" checked={consent2} onChange={e => setConsent2(e.target.checked)} />
                <span>{t('login.consent2')}</span>
              </label>
            </div>

            {/* Zorunlu yasal onaylar — panoram.com.tr "Üye Ol" akışıyla aynı desen: işaretlenmeden
                hesap oluşturulamaz (bkz. handleRegister ve backend routes/auth.js). */}
            <div className="auth-consent-box">
              <label className="auth-consent-row">
                <input type="checkbox" checked={agreeKvkk} onChange={e => setAgreeKvkk(e.target.checked)} required />
                <span>
                  <button type="button" className="auth-legal-link" onClick={() => setShowKvkk(true)}>{t('login.kvkkModalTitle')}</button>
                  {t('login.agreeKvkkPost')}
                </span>
              </label>
            </div>
            <div className="auth-consent-box">
              <label className="auth-consent-row">
                <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} required />
                <span>
                  <button type="button" className="auth-legal-link" onClick={() => setShowTerms(true)}>{t('login.termsModalTitle')}</button>
                  {t('login.agreeTermsMid')}
                  <button type="button" className="auth-legal-link" onClick={() => setShowPrivacy(true)}>{t('login.privacyModalTitle')}</button>
                  {t('login.agreeTermsPost')}
                </span>
              </label>
            </div>

            <button type="submit" className="auth-btn-primary" disabled={loading || !agreeKvkk || !agreeTerms}>
              {loading ? t('login.registering') : t('login.registerButton')}
            </button>
          </form>
        )}

        {/* Google */}
        {!tfa && <>
        <div className="auth-divider"><span>{t('login.or')}</span></div>
        <button type="button" className="auth-btn-google" onClick={handleGoogle}>
          <GoogleIcon />
          <span>{t('login.continueWithGoogle')}</span>
        </button>
        </>}

      </div>

      {showKvkk    && <KvkkNoticeModal onClose={() => setShowKvkk(false)} />}
      {showTerms   && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}
